import { mapCollection } from '../../utils/collection.utils';
import { parseNumber } from '../../utils/number.utils';
import { ModelCurrentTask } from '../summary/model-current-task';
import { ModelCurrentTaskProgram } from '../summary/model-current-task-program';
import { ModelCurrentTaskProgramDetail } from '../summary/model-current-task-program-detail';
import { ModelSummary } from '../summary/model-summary';
import { ModelSummaryEvent } from '../summary/model-summary-event';
import { ModelSummaryOee } from '../summary/model-summary-oee';
import { ModelSummaryProgram } from '../summary/model-summary-program';
import { ModelSummaryStatus } from '../summary/model-summary-status';
import { ModelSummaryTime } from '../summary/model-summary-time';
import { ModelTime } from '../summary/model-time';
import { ModelTimeline } from '../summary/model-timeline';
import { ModelTimelineDetail } from '../summary/model-timeline-detail';
import { ModelCurrentTaskIncidenceTotalsBuilder } from './model-current-task-incidence-totals.builder';
import { ModelCurrentTaskPiecesTimeBuilder } from './model-current-task-pieces-time.builder';
import { ModelBuilder } from './model.builder';
import { PauseBuilder } from './pause.builder';

export class SummaryBuilder {

  static modelSummaryStatusFromJson(json, diff: number) {
    return (json)
      ? new ModelSummaryStatus(
        json.hash,
        parseNumber(json.startTime),
        json.activityName,
        json.subactivityName,
        json.programReference,
        parseNumber(json.programTime),
        json.running,
        json.execution,
        json.preparation,
        json.alarm,
        json.stop,
        json.warning,
        json.maintenance,
        json.pause,
        parseNumber(json.pauseStartTime),
        parseNumber(json.totalPauseTime),
        diff
      )
      : null;
  }

  static modelSummaryProgramFromJson(json, diff: number) {
    return (json)
      ? new ModelSummaryProgram(
        json.hash,
        parseNumber(json.startTime),
        json.programHash,
        json.programReference,
        parseNumber(json.programTime),
        json.programTool,
        json.programToolHolder,
        json.printHash,
        json.printReference,
        json.manufacturingOrderHash,
        json.manufacturingOrderCode,
        json.manufacturingOrderCustomer,
        json.manufacturingOrderSubCustomer,
        json.manufacturingOrderReference,
        json.manufacturingOrderProject,
        json.manufacturingOrderIsPdfExist,
        json.manufacturingOrderIsClaimed,
        json.manufacturingOrderIsDeliveryChanged,
        json.materialHash,
        json.materialDenomination,
        json.materialFileName,
        json.operationHash,
        json.operationName,
        json.sourceHash,
        json.sourceName,
        json.partHash,
        json.partName,
        json.processCode,
        diff
      )
      : null;
  }

  static modelSummaryTimeFromJson(json) {
    return (json)
      ? new ModelSummaryTime(
        parseNumber(json.date),
        parseNumber(json.running),
        parseNumber(json.preparation),
        parseNumber(json.stop),
        parseNumber(json.alarm),
        parseNumber(json.maintenance),
        parseNumber(json.pause)
      )
      : null;
  }

  static modelSummaryOeeFromJson(json) {
    return (json)
      ? new ModelSummaryOee(
        json.hash,
        parseNumber(json.date),
        parseNumber(json.availability),
        parseNumber(json.performance),
        parseNumber(json.quality),
        parseNumber(json.oee),
        parseNumber(json.oeeTrend),
        json.current,
      )
      : null;
  }

  static modelSummaryOeeFromList(list) {
    return mapCollection(SummaryBuilder.modelSummaryOeeFromJson, list);
  }

  static modelSummaryFromJson(json, diff: number) {
    return (json)
      ? new ModelSummary(
        ModelBuilder.activeModelFromJson(json.model),
        SummaryBuilder.modelSummaryStatusFromJson(json.status, diff),
        SummaryBuilder.modelSummaryProgramFromJson(json.program, diff),
        SummaryBuilder.modelSummaryTimeFromJson(json.schedulerTaskTimes),
        SummaryBuilder.modelSummaryTimeFromJson(json.schedulerTaskTimesTrend),
        SummaryBuilder.modelSummaryTimeFromJson(json.daily),
        SummaryBuilder.modelSummaryTimeFromJson(json.week),
        SummaryBuilder.modelSummaryOeeFromJson(json.oee),
        ModelCurrentTaskIncidenceTotalsBuilder.fromJson(json.totals),
        ModelCurrentTaskPiecesTimeBuilder.fromJson(json.piecesTime),
        json.schedulerTaskHash
      )
      : null;
  }

  static modelSummariesFromList(list, diff: number) {
    const createSummary = summary => SummaryBuilder.modelSummaryFromJson(summary, diff);

    return mapCollection(createSummary, list);
  }

  static modelTimeFromJson(json) {
    return (json)
      ? new ModelTime(
        parseNumber(json.date),
        parseNumber(json.runningTime),
        parseNumber(json.preparationTime),
        parseNumber(json.stopTime),
        parseNumber(json.alarmTime),
        parseNumber(json.maintenanceTime)
      )
      : null;
  }

  static modelTimesFromList(list) {
    return mapCollection(SummaryBuilder.modelTimeFromJson, list);
  }

  static modelTimelineFromJson(json) {
    return (json)
      ? new ModelTimeline(
        json.hash,
        parseNumber(json.startTime),
        parseNumber(json.endTime),
        json.execution,
        json.preparation,
        json.alarm,
        json.stop,
        json.maintenance,
        json.pause,
        json.inactive
      )
      : null;
  }

  static modelTimelinesFromList(list) {
    return mapCollection(SummaryBuilder.modelTimelineFromJson, list);
  }

  static modelSummaryEventFromJson(json) {
    return (json)
      ? new ModelSummaryEvent(
        json.hash,
        parseNumber(json.startTime),
        parseNumber(json.endTime),
        json.eventName
      )
      : null;
  }

  static modelSummaryEventsFromList(list) {
    return mapCollection(SummaryBuilder.modelSummaryEventFromJson, list);
  }

  static modelTimelineDetailFromJson(json) {
    return (json)
      ? new ModelTimelineDetail(
        json.hash,
        parseNumber(json.startTime),
        parseNumber(json.endTime),
        json.manufacturingOrderCode,
        json.manufacturingOrderReference,
        json.manufacturingOrderProject,
        json.materialFilename,
        json.materialDenomination,
        json.printReference,
        json.sourceName,
        json.partName,
        json.operationName,
        json.programReference,
        json.programTool,
        json.programToolHolder,
        json.execution,
        json.preparation,
        json.alarm,
        json.stop,
        json.maintenance,
        json.pause,
        json.eventName,
        json.message
      )
      : null;
  }

  static modelCurrentTaskFromJson(json) {
    return (json)
      ? new ModelCurrentTask(
        json.hash,
        parseNumber(json.startTime),
        parseNumber(json.identifier),
        json.waiting,
        json.end,
        json.alarm,
        json.pause,
        SummaryBuilder.modelCurrentTaskProgramFromList(json.programs)
      )
      : null;
  }

  static modelCurrentTaskProgramFromJson(json) {
    return (json)
      ? new ModelCurrentTaskProgram(
        json.hash,
        parseNumber(json.time),
        parseNumber(json.index),
        json.current,
        json.subactivityHash,
        json.subactivityName,
        parseNumber(json.startTime),
        parseNumber(json.endTime),
        json.disabled,
        PauseBuilder.fromList(json.pauses)
      )
      : null;
  }

  static modelCurrentTaskProgramFromList(list) {
    return mapCollection(SummaryBuilder.modelCurrentTaskProgramFromJson, list);
  }

  static modelCurrentTaskProgramDetailFromJson(json) {
    return (json)
      ? new ModelCurrentTaskProgramDetail(
        json.hash,
        parseNumber(json.startTime),
        parseNumber(json.endTime),
        json.manufacturingOrderCode,
        json.manufacturingOrderReference,
        json.manufacturingOrderProject,
        json.materialFilename,
        json.materialDenomination,
        json.printReference,
        json.sourceName,
        json.partName,
        json.operationName,
        json.programReference,
        json.programTool,
        json.programToolHolder,
        json.execution,
        json.eventName,
        json.message
      )
      : null;
  }
}



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/core/models/builders/summary.builder.ts